import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionSummary as MuiAccordionSummary, AccordionDetails as MuiAccordionDetails, Typography, List, ListItem, ListItemText } from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { styled as muiStyled } from '@mui/material/styles';
import styled from 'styled-components';
import YouTubePlayer from './YtPlayer';
import Hls from 'hls.js';

import COLORS from "utils/constants/color"
import SentryWrap from 'components/Sentry';
const Section = styled.div`
  border-bottom: 1px dashed ${COLORS.black700};
  padding: 0px 0px 24px;
  margin-bottom: 16px;
  position: relative;
  &:first-child {
    border-bottom: none;
    margin-bottom: 0px;
    padding: 0px;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0px;
  }
`;

const AccordionSummary = styled(MuiAccordionSummary)`
  background-color: #03989e;
  color: white;
  text-transform: capitalize;
  border-bottom: 1px solid white;
  margin-bottom: -1px;
  min-height: 30px;

  &.Mui-expanded {
    min-height: 30px;
  }

  .MuiAccordionSummary-content.Mui-expanded {
    margin: 8px 0;
  }
`;

const AccordionDetails = muiStyled(MuiAccordionDetails)(({ theme }) => ({
    padding: '2px',
}));

const StyledList = muiStyled(List)(({ theme }) => ({
    width: '100%',
    maxWidth: 'auto',
    backgroundColor: theme.palette.background.paper,
    '& .MuiListItem-padding': {
        paddingBottom: '0px',
        paddingTop: '0px'
    },
    '& .MuiListItem-root': {
        marginTop: '0px',
        marginBottom: '0px',
        textAlign: 'justify'
    },
    '& .MuiListItem-gutters': {
        paddingLeft: '3px',
        paddingRight: '1px'
    }
}));

const ScrollContainer = muiStyled('div')({
    height: 'fit-content',
    display: 'flex',
    justifyContent: 'center'
});

const Video = styled.video`
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  height: auto;
  outline: none;
  width: 100%;
  max-height:300px;
`;

const VideoSupport = ({ showSpecialSupport, videoSupport, showCopyRight }) => {
    const [expanded, setExpanded] = useState(false);
    const videoRef = useRef(null);
    const dataUrl = useMemo(() =>Array.isArray(videoSupport) && videoSupport[0]?.content, [videoSupport]);
    const isHls = useMemo(() => typeof dataUrl === 'string' && dataUrl.includes('.m3u8'), [dataUrl]);
    const hlsPoster = useMemo(() => {
        if(isHls){
            const videoId = dataUrl.split('/')[3];
            if(videoId){
                return `https://customer-quvpuvq5vklyms5a.cloudflarestream.com/${videoId}/thumbnails/thumbnail.jpg`;
            }
        }
        return null;
    }, [dataUrl, isHls]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !isHls) return;

        let hls;

        if (Hls.isSupported()) {
            hls = new Hls({ lowLatencyMode: true });
            hls.loadSource(dataUrl);
            hls.attachMedia(video);

            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS.js error:', data);
            });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = dataUrl;
        }

        return () => {
            if (hls) {
                hls.destroy();
            }
        };
    }, [dataUrl, isHls]);
    if (!showSpecialSupport || videoSupport.length === 0) return null;
    if(!dataUrl) return null;
    return (
        <SentryWrap>
        <Accordion square expanded={expanded} onChange={() => setExpanded(!expanded)}>
            <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                <Typography>Video Support</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Section className="playAudio">
                    <Video 
                        ref={videoRef}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            showCopyRight();
                        }} 
                        controls 
                        controlsList="nodownload" 
                        width="100%"
                        poster={hlsPoster}
                        preload="metadata"
                    >
                        {!isHls && <source src={dataUrl} type="video/mp4" />}
                    </Video>
                </Section>
            </AccordionDetails>
        </Accordion>
        </SentryWrap>
    );
};

const TipVideo = ({ videoUrl, showCopyRight }) => {
    const videoRef = useRef(null);
    
    const dataUrl = useMemo(() => videoUrl, [videoUrl]);
    const isHls = useMemo(() => typeof dataUrl === 'string' && dataUrl.includes('.m3u8'), [dataUrl]);
    const hlsPoster = useMemo(() => {
        if(isHls){
            const videoId = dataUrl.split('/')[3];
            if(videoId){
                return `https://customer-quvpuvq5vklyms5a.cloudflarestream.com/${videoId}/thumbnails/thumbnail.jpg`;
            }
        }
        return null;
    }, [dataUrl, isHls]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !isHls) return;

        let hls;

        if (Hls.isSupported()) {
            hls = new Hls({ lowLatencyMode: true });
            hls.loadSource(dataUrl);
            hls.attachMedia(video);

            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS.js error:', data);
            });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = dataUrl;
        }

        return () => {
            if (hls) {
                hls.destroy();
            }
        };
    }, [dataUrl, isHls]);

    return (
        <Video 
            ref={videoRef}
            onContextMenu={(e) => {
                e.preventDefault();
                showCopyRight();
            }} 
            controls 
            controlsList="nodownload" 
            width="100%"
            poster={hlsPoster}
            preload="metadata"
        >
            {!isHls && <source src={videoUrl} type="video/mp4" />}
        </Video>
    );
};

const TeacherStrategies = ({ teacher_strategies, tips, showCopyRight }) => {
    const [expanded, setExpanded] = useState(true);

    if (!teacher_strategies || tips.length === 0) return null;
    
    return (
        <Accordion square expanded={expanded} onChange={() => setExpanded(!expanded)}>
            <AccordionSummary aria-controls="panel2d-content" id="panel2d-header" style={{ height: 30 }}>
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                    <Typography>Teacher Strategies</Typography>
                    {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <ScrollContainer>
                    <StyledList component="nav" aria-label="contacts">
                        {tips.map((item, key) => (
                            item.textTip && (
                                <ListItem key={key} button>
                                    <ListItemText disableTypography style={{ color: item.color }} primary={item.textTip} />
                                </ListItem>
                            )
                        ))}
                        {tips.map((item, key) => (
                            item.video && (
                                <Section className="playAudio" key={key}>
                                    <TipVideo videoUrl={item.video} showCopyRight={showCopyRight} />
                                </Section>
                            )
                        ))}
                        {tips.map((item, key) => (
                            item.yt_video && (
                                <Section className="playAudio" key={key}>
                                    <YouTubePlayer url={item.yt_video} style={{ width: 350 }} />
                                </Section>
                            )
                        ))}
                    </StyledList>
                </ScrollContainer>
            </AccordionDetails>
        </Accordion>
    );
};

export { VideoSupport, TeacherStrategies };